import { createContext, useContext, useState, ReactNode, useEffect } from "react";
import { useAuth } from "./AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { generateSalt } from "@/lib/encryption";
import { useToast } from "@/hooks/use-toast";

interface EncryptionContextType {
  encryptionEnabled: boolean;
  encryptionKey: string | null;
  encryptionSalt: string | null;
  enableEncryption: (password: string) => Promise<void>;
  disableEncryption: () => Promise<void>;
  setEncryptionKey: (key: string) => void;
}

const EncryptionContext = createContext<EncryptionContextType | undefined>(undefined);

export const EncryptionProvider = ({ children }: { children: ReactNode }) => {
  const [encryptionEnabled, setEncryptionEnabled] = useState(false);
  const [encryptionKey, setEncryptionKeyState] = useState<string | null>(null);
  const [encryptionSalt, setEncryptionSalt] = useState<string | null>(null);
  const { user } = useAuth();
  const { toast } = useToast();

  useEffect(() => {
    if (user) {
      loadEncryptionSettings();
    }
  }, [user]);

  const loadEncryptionSettings = async () => {
    if (!user) return;

    const { data, error } = await supabase
      .from("profiles")
      .select("encryption_enabled, encryption_salt")
      .eq("id", user.id)
      .single();

    if (error) {
      console.error("Error loading encryption settings:", error);
      return;
    }

    setEncryptionEnabled(data?.encryption_enabled || false);
    setEncryptionSalt(data?.encryption_salt || null);
  };

  const enableEncryption = async (password: string) => {
    if (!user) throw new Error("User not authenticated");

    const salt = generateSalt();

    const { error } = await supabase
      .from("profiles")
      .update({
        encryption_enabled: true,
        encryption_salt: salt,
      })
      .eq("id", user.id);

    if (error) {
      toast({
        title: "Error enabling encryption",
        description: error.message,
        variant: "destructive",
      });
      throw error;
    }

    setEncryptionEnabled(true);
    setEncryptionSalt(salt);
    setEncryptionKeyState(password);

    toast({
      title: "Encryption enabled",
      description: "Your data will now be encrypted end-to-end",
    });
  };

  const disableEncryption = async () => {
    if (!user) throw new Error("User not authenticated");

    const { error } = await supabase
      .from("profiles")
      .update({
        encryption_enabled: false,
        encryption_salt: null,
      })
      .eq("id", user.id);

    if (error) {
      toast({
        title: "Error disabling encryption",
        description: error.message,
        variant: "destructive",
      });
      throw error;
    }

    setEncryptionEnabled(false);
    setEncryptionSalt(null);
    setEncryptionKeyState(null);

    toast({
      title: "Encryption disabled",
      description: "Your data will no longer be encrypted",
      variant: "destructive",
    });
  };

  const setEncryptionKey = (key: string) => {
    setEncryptionKeyState(key);
  };

  return (
    <EncryptionContext.Provider
      value={{
        encryptionEnabled,
        encryptionKey,
        encryptionSalt,
        enableEncryption,
        disableEncryption,
        setEncryptionKey,
      }}
    >
      {children}
    </EncryptionContext.Provider>
  );
};

export const useEncryption = () => {
  const context = useContext(EncryptionContext);
  if (context === undefined) {
    throw new Error("useEncryption must be used within an EncryptionProvider");
  }
  return context;
};
